import { useCallback, useMemo, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { getDownloadURL, ref } from 'firebase/storage';
import type {
  CncSpeciesAssignment,
  ProposalBuilderVersionDoc,
  ProposalNodeSet,
  RfqHubFileRelease,
  RfqPackage,
  RfqProject,
  RfqSubmission,
  SupplierOrg,
  TimberSupplierStructureRef,
} from '../types';
import { useAuth } from '../utils/AuthContext';
import { packageCoversHubRelease, packageHubReleaseIds } from '../utils/rfqPackageReleases';
import { useCRM } from '../utils/CRMContext';
import { db, storage } from '../utils/firebase';
import { listProposalVersions } from '../utils/proposalVersioning';
import {
  loadHubTimberListNodeSets,
  loadHubTimberListPublishPreview,
  loadPackageTimberListNodeSets,
  loadPackageTimberListPublishPreview,
  loadStagedTimberListNodeSets,
  loadStagedTimberListPublishPreview,
} from '../utils/rfqHubTimberImport';
import { getRfqHubFileRelease, promoteHubReleaseToVendor } from '../utils/rfqHubFileReleaseService';
import { publishLinkFieldsFromHubRelease, formatProposalRfqLinkText, proposalRfqLinkFromRelease } from '../utils/proposalRfqVersionLink';
import {
  filterTargetsWithoutHubReleasePackage,
  hubReleaseAlreadyPublishedForTargets,
  nextVendorPackageVersionIndex,
} from '../utils/vendorPackageVersion';
import { rfqProjectSupplierOrgIds } from '../utils/rfqProjectSuppliers';
import {
  rfqProjectTimberSupplierSpecies,
  rfqProjectTimberSpeciesDrynessEntries,
} from '../utils/timberRfqSuppliers';
import { inviteHasStructureScope } from '../utils/timberRfqStructureScope';
import { stagedReleaseStructureRefs } from '../utils/rfqPublishStructureReview';
import { isReferenceRelease } from '../utils/rfqReleasePurpose';
import { scopeChangeSummary } from '../utils/rfqPackageScopeDrift';
import {
  normalizeStructureSpecies,
  speciesForStructure,
  structureSpeciesRulesFromInvite,
} from '../utils/timberRfqStructureSpecies';
import { resendRfqPackageScopeChange } from '../utils/rfqPackageScopeResendApi';
import { acknowledgeRfqPackageScopeDrift } from '../utils/rfqPackageScopeAcknowledgeApi';
import { cncInviteBoardFeet, inheritCncMaterialFlowFromPackages, sanitizeCncSpeciesAssignments } from '../utils/cncRfqAssignments';
import { patchTimberSupplierInvite } from '../utils/cncInviteInputEdit';
import {
  buildShipToByOrgId,
  inviteShipToConfig,
  rfqProjectSupplierShipTo,
  shipToContextForWorkType,
} from '../utils/supplierShipTo';
import { updateTimberQuoteSubmission, type TimberQuoteSubmissionEdits } from '../utils/rfqTimberQuoteEdit';
import { parseLegacyTitle } from '../utils/projectName';
import { projectSiteInputFromProjectAndDeal } from '../utils/projectAddressService';
import { resolveOrgsMissingRfqNotificationFirstName } from '../utils/vendorOrgSummary';
import { resolveRfqSupplierProjectLabel } from '../utils/rfqPublishEmailPreview';
import { structureRefsFromNodeSets } from '../utils/timberQuoteStreamKey';
import { buildWorkTypeSummary, selectRfqProjectForDeal } from '../utils/vendorAdminProjectSummary';
import type { SupplierPublishReviewData } from '../components/vendor/SupplierPublishReviewModal';

function cncAssignmentsCarriedForward(
  assignments: CncSpeciesAssignment[] | undefined,
  packages: RfqPackage[],
  projectId: string,
  orgId: string,
): CncSpeciesAssignment[] | undefined {
  if (!assignments?.length) return assignments;
  return sanitizeCncSpeciesAssignments(
    inheritCncMaterialFlowFromPackages(
      assignments,
      packages.filter(
        (pkg) => pkg.rfqProjectId === projectId && (pkg.workType ?? 'timber_rfq') === 'cnc_quote',
      ),
      orgId,
    ) ?? assignments,
  );
}

function cncMaterialFlowChanged(
  before: CncSpeciesAssignment[] | undefined,
  after: CncSpeciesAssignment[] | undefined,
): boolean {
  const left = before ?? [];
  const right = after ?? [];
  if (left.length !== right.length) return true;
  return left.some((row, i) => {
    const next = right[i];
    return row.materialSource !== next?.materialSource
      || (row.sourceSupplierOrgId || '') !== (next?.sourceSupplierOrgId || '')
      || (row.sourceSupplierName || '') !== (next?.sourceSupplierName || '');
  });
}

export const RFQ_PUBLISH_FROM_HUB = '__hub_timber__';
export const RFQ_PUBLISH_STAGED_PREFIX = '__hub_staged__';

/** Synthetic lead id for quote PDF viewer when the deal has no CRM lead. */
export const RFQ_QUOTE_VIEWER_LEAD_ID = '__rfq_quote__';

export type RfqSubmissionPdfViewerState = {
  url: string;
  title: string;
  documentId: string;
  leadId: string;
  dealId: string;
  projectId?: string;
};

export function parseStagedReleasePick(pick: string): string | null {
  if (!pick.startsWith(RFQ_PUBLISH_STAGED_PREFIX)) return null;
  const id = pick.slice(RFQ_PUBLISH_STAGED_PREFIX.length).trim();
  return id || null;
}

type PendingTimberPublish = {
  kind: 'timber';
  proj: RfqProject;
  targets: string[];
  pick: string;
  preload: { nodeSets: ProposalNodeSet[]; sourceFileName: string };
};

type PendingCncPublish = {
  kind: 'cnc';
  proj: RfqProject;
  targets: string[];
  releaseId: string;
};

type PendingScopeChangeResend = {
  kind: 'scope_change';
  proj: RfqProject;
  orgId: string;
  /** Already-published package being updated in place. */
  packageId: string;
  workType: 'timber_rfq' | 'cnc_quote';
  /** New scope refs from the invite; empty = all structures. */
  scopeStructures: TimberSupplierStructureRef[];
  /** Timber only — full release node sets; re-scoped on confirm. */
  releaseNodeSets?: ProposalNodeSet[];
  /** CNC only — invite payload replacements for the callable. */
  cncSpeciesAssignments?: CncSpeciesAssignment[];
  cncBoardFeet?: number;
  addedStructureNames: string[];
  removedStructureNames: string[];
  /** Timber only — invite species per structure to snapshot onto the package (null = clear). */
  structureSpecies?: Record<string, string[]> | null;
  speciesChangeLines?: string[];
};

type PendingPublish = PendingTimberPublish | PendingCncPublish | PendingScopeChangeResend;

export type PublishReviewState = {
  open: boolean;
  loading: boolean;
  error: string | null;
  data: SupplierPublishReviewData | null;
  pending: PendingPublish | null;
};

export const CLOSED_PUBLISH_REVIEW: PublishReviewState = {
  open: false,
  loading: false,
  error: null,
  data: null,
  pending: null,
};

export function useDealSupplierRfqWorkflow(dealId: string | null) {
  const { user } = useAuth();
  const {
    supplierOrgs,
    supplierInvites,
    timberProjects,
    rfqProjects,
    rfqPackages,
    rfqSubmissions,
    updateRfqProject,
    publishRfqPackage,
    deleteRfqPackage,
    deleteRfqSubmission,
    updateRfqSubmission,
    enhancedDeals,
  } = useCRM();

  const [versionPickByProject, setVersionPickByProject] = useState<Record<string, string>>({});
  const [versionsByDeal, setVersionsByDeal] = useState<Record<string, ProposalBuilderVersionDoc[]>>({});
  const [publishBusyId, setPublishBusyId] = useState<string | null>(null);
  const [publishReview, setPublishReview] = useState<PublishReviewState>(CLOSED_PUBLISH_REVIEW);
  const [packageTitleDraftByProject, setPackageTitleDraftByProject] = useState<Record<string, string>>({});

  const supplierOrgById = useMemo(() => {
    const m = new Map<string, SupplierOrg>();
    supplierOrgs.forEach(o => {
      if (o.id) m.set(o.id, o);
    });
    return m;
  }, [supplierOrgs]);

  const selectedDeal = useMemo(
    () => (dealId ? enhancedDeals.find(d => d.id === dealId) : undefined),
    [enhancedDeals, dealId],
  );

  const selectedTimberProject = useMemo(() => {
    if (!dealId) return undefined;
    return (
      timberProjects.find(
        p =>
          p.dealId === dealId &&
          (p.phase === 'contracted' || p.phase === undefined),
      ) || timberProjects.find(p => p.dealId === dealId)
    );
  }, [timberProjects, dealId]);

  const projectDisplay = useMemo(() => {
    const num = (selectedDeal?.projectNumber || '').trim();
    const numberDisplay = num ? (num.startsWith('#') ? num : `#${num}`) : '';
    const descriptor = (
      (selectedDeal?.descriptor || '').trim() ||
      parseLegacyTitle(selectedDeal?.title || '').descriptor ||
      selectedTimberProject?.name ||
      selectedDeal?.title ||
      ''
    ).trim();
    const clientLabel = (
      selectedDeal?.clientLabel ||
      selectedTimberProject?.clientName ||
      selectedDeal?.contactName ||
      ''
    ).trim();
    return { numberDisplay, descriptor, clientLabel, location: selectedTimberProject?.location };
  }, [selectedDeal, selectedTimberProject]);

  const dealTitleForRfqPublish = useCallback(
    (proj: RfqProject) =>
      resolveRfqSupplierProjectLabel({
        projectNumber: projectDisplay.numberDisplay,
        descriptor: projectDisplay.descriptor,
        dealTitleFallback: proj.dealTitle,
        dealIdFallback: proj.dealId,
      }),
    [projectDisplay],
  );

  const projectSite = useMemo(
    () => projectSiteInputFromProjectAndDeal(selectedTimberProject, selectedDeal),
    [selectedDeal, selectedTimberProject],
  );

  const timberProject = useMemo(
    () => (dealId ? selectRfqProjectForDeal(dealId, 'timber_rfq', rfqProjects) : null),
    [rfqProjects, dealId],
  );

  const cncProject = useMemo(
    () => (dealId ? selectRfqProjectForDeal(dealId, 'cnc_quote', rfqProjects) : null),
    [rfqProjects, dealId],
  );

  const otherProject = useMemo(
    () => (dealId ? selectRfqProjectForDeal(dealId, 'other_quote', rfqProjects) : null),
    [rfqProjects, dealId],
  );

  const dealVendorSummaries = useMemo(() => {
    if (!dealId) return null;
    return {
      timber: buildWorkTypeSummary(
        dealId,
        'timber_rfq',
        rfqProjects,
        rfqPackages,
        rfqSubmissions,
        supplierOrgById,
      ),
      cnc: buildWorkTypeSummary(
        dealId,
        'cnc_quote',
        rfqProjects,
        rfqPackages,
        rfqSubmissions,
        supplierOrgById,
      ),
    };
  }, [dealId, rfqProjects, rfqPackages, rfqSubmissions, supplierOrgById]);

  const ensureTargetsHaveNotificationFirstName = useCallback(
    (targets: string[]): boolean => {
      const missing = resolveOrgsMissingRfqNotificationFirstName(targets, supplierOrgById);
      if (!missing.length) return true;
      alert(
        `Add an RFQ notification first name in Vendor Management for: ${missing.map(m => m.orgName).join(', ')}. RFQ emails cannot send without it.`,
      );
      return false;
    },
    [supplierOrgById],
  );

  const [submissionPdfViewer, setSubmissionPdfViewer] = useState<RfqSubmissionPdfViewerState | null>(
    null,
  );

  const openSubmissionFile = useCallback(async (sub: RfqSubmission) => {
    if (sub.notBidding) {
      alert('This response is “not bidding” — there is no file to open.');
      return;
    }
    if (!sub.storagePath) {
      alert('No file path on record.');
      return;
    }
    try {
      const url = await getDownloadURL(ref(storage, sub.storagePath));
      const leadId = (selectedDeal?.leadId || '').trim() || RFQ_QUOTE_VIEWER_LEAD_ID;
      setSubmissionPdfViewer({
        url,
        title: sub.fileName?.trim() || 'Supplier quote',
        documentId: sub.id || sub.storagePath,
        leadId,
        dealId: sub.dealId,
        projectId: sub.rfqProjectId,
      });
    } catch (e) {
      console.error(e);
      alert('Could not open the file.');
    }
  }, [selectedDeal?.leadId]);

  const closeSubmissionPdfViewer = useCallback(() => setSubmissionPdfViewer(null), []);

  const deleteSubmission = useCallback(
    async (sub: RfqSubmission, rfqProject: RfqProject) => {
      if (!sub.id) return;
      await deleteRfqSubmission({
        id: sub.id,
        storagePath: sub.storagePath,
        notBidding: sub.notBidding,
        quotePlaceholder: sub.quotePlaceholder,
        linkedTimberSubmissionId: sub.linkedTimberSubmissionId,
        linkedCncSubmissionId: sub.linkedCncSubmissionId,
      });
      if (!rfqProject.id) return;
      const remaining = rfqSubmissions.filter(
        s => s.rfqProjectId === rfqProject.id && s.id !== sub.id,
      );
      const responded = remaining.some(
        s => !s.notBidding && (!!s.storagePath || !!s.cncQuote),
      );
      await updateRfqProject(rfqProject.id, { status: responded ? 'quoted' : 'active' });
    },
    [deleteRfqSubmission, rfqSubmissions, updateRfqProject],
  );

  const updateQuoteSubmission = useCallback(
    async (sub: RfqSubmission, rfqProject: RfqProject, orgId: string, edits: TimberQuoteSubmissionEdits) => {
      await updateTimberQuoteSubmission(
        { updateRfqSubmission, updateRfqProject },
        rfqProject,
        orgId,
        sub,
        edits,
      );
    },
    [updateRfqProject, updateRfqSubmission],
  );

  const loadVersions = useCallback(
    async (targetDealId: string) => {
      if (versionsByDeal[targetDealId]) return;
      const list = await listProposalVersions(targetDealId, 40);
      setVersionsByDeal(prev => ({ ...prev, [targetDealId]: list }));
    },
    [versionsByDeal],
  );

  const handleSyncHubLink = useCallback(
    async (proj: RfqProject) => {
      if (!proj.id || !proj.dealId) return;
      const hub =
        timberProjects.find(
          p =>
            p.dealId === proj.dealId &&
            (p.phase === 'contracted' || p.phase === undefined),
        ) || timberProjects.find(p => p.dealId === proj.dealId);
      if (!hub?.id) {
        alert('No Project Hub timber project found for this deal yet.');
        return;
      }
      await updateRfqProject(proj.id, { timberProjectId: hub.id, timberProjectName: hub.name });
    },
    [timberProjects, updateRfqProject],
  );

  const filterPublishTargets = useCallback((proj: RfqProject, targets: string[]) => {
    const allowed = rfqProjectSupplierOrgIds(proj);
    return targets.filter(id => allowed.includes(id));
  }, []);

  const pendingPublishTargetsForRelease = useCallback(
    (proj: RfqProject, hubFileReleaseId: string, targets: string[]) => {
      if (!proj.id) return [];
      const allowed = filterPublishTargets(proj, targets);
      return filterTargetsWithoutHubReleasePackage(
        rfqPackages,
        proj.id,
        hubFileReleaseId,
        allowed,
      );
    },
    [filterPublishTargets, rfqPackages],
  );

  const publishReviewSharedFields = useCallback(
    (proj: RfqProject, targets: string[], workType: 'timber_rfq' | 'cnc_quote') => {
      const shipCtx = shipToContextForWorkType(workType, timberProject, cncProject);
      return {
      projectNumber: projectDisplay.numberDisplay || undefined,
      projectName: projectDisplay.descriptor || undefined,
      supplierSpeciesByOrg: Object.fromEntries(
        targets.map(orgId => {
          const species =
            workType === 'cnc_quote'
              ? []
              : rfqProjectTimberSupplierSpecies(proj, orgId);
          return [orgId, species];
        }),
      ),
      supplierSpeciesDrynessByOrg: Object.fromEntries(
        targets.map(orgId => [orgId, rfqProjectTimberSpeciesDrynessEntries(proj, orgId)]),
      ),
      supplierStructuresByOrg: Object.fromEntries(
        targets.map(orgId => {
          const invite = proj.timberSupplierInvites?.find(row => row.supplierOrgId === orgId);
          return [orgId, inviteHasStructureScope(invite) ? invite!.structures! : null];
        }),
      ),
      cncAssignmentsByOrg: Object.fromEntries(
        targets.map(orgId => {
          const invite = proj.timberSupplierInvites?.find(row => row.supplierOrgId === orgId);
          const carried = proj.id
            ? cncAssignmentsCarriedForward(invite?.cncSpeciesAssignments, rfqPackages, proj.id, orgId)
            : invite?.cncSpeciesAssignments;
          return [orgId, carried?.length ? carried : null];
        }),
      ),
      supplierShipToByOrg: Object.fromEntries(
        targets.map(orgId => [
          orgId,
          rfqProjectSupplierShipTo(proj, orgId, {
            ...shipCtx,
            orgId,
            projectSite,
          }),
        ]),
      ),
      supplierShipToPresetByOrg: Object.fromEntries(
        targets.map(orgId => {
          const invite = proj.timberSupplierInvites?.find(row => row.supplierOrgId === orgId);
          return [
            orgId,
            inviteShipToConfig(invite, { ...shipCtx, orgId }).preset,
          ];
        }),
      ),
    };
    },
    [projectDisplay, projectSite, timberProject, cncProject, rfqPackages],
  );

  const publishShipToPayload = useCallback(
    (proj: RfqProject, targets: string[], workType: 'timber_rfq' | 'cnc_quote') => {
      const shipCtx = shipToContextForWorkType(workType, timberProject, cncProject);
      const shipToByOrgId = buildShipToByOrgId(proj, targets, {
        ...shipCtx,
        projectSite,
      });
      const shipToPresetByOrgId = Object.fromEntries(
        targets.map(orgId => {
          const invite = proj.timberSupplierInvites?.find(row => row.supplierOrgId === orgId);
          return [orgId, inviteShipToConfig(invite, { ...shipCtx, orgId }).preset];
        }),
      );
      return { shipToByOrgId, shipToPresetByOrgId };
    },
    [projectSite, timberProject, cncProject],
  );

  const handlePublish = useCallback(
    async (
      proj: RfqProject,
      targets: string[],
      hubPreload?: { nodeSets: ProposalNodeSet[]; sourceFileName: string } | null,
      pickOverride?: string,
      customEmailMessage?: string,
      scopeStructuresByOrg?: Record<string, TimberSupplierStructureRef[]>,
    ): Promise<boolean> => {
      if (!proj.id || !user?.id) return false;
      const targetDealId = proj.dealId;
      const pick = pickOverride ?? (versionPickByProject[proj.id] || 'live');
      if (!targets.length) {
        alert('Select at least one supplier to publish to.');
        return false;
      }
      setPublishBusyId(proj.id);
      try {
        let proposalVersionId: string | null = null;
        let proposalSnapshot: { nodeSets: ProposalNodeSet[] } | undefined;
        let timberListSource: 'proposal_live' | 'proposal_saved_version' | 'hub_csv' | 'hub_staged_release' =
          'proposal_live';
        let hubFileReleaseId: string | undefined;
        let releaseDisplayLabel: string | undefined;
        let defaultLabel = 'Published from live proposal';
        let linkedProposalVersionLabel: string | undefined;
        let linkedProposalVersionSource: import('../types').ProposalVersionSource | undefined;
        let linkedProposalVersionSavedAt: Date | undefined;
        let linkedFrom: 'proposal_push' | 'hub_file_stage' | undefined;

        const stagedReleaseId = parseStagedReleasePick(pick);

        if (pick === 'live') {
          const snap = await getDoc(doc(db, 'proposalBuilders', targetDealId));
          if (!snap.exists()) {
            alert('No live proposal found for this deal.');
            return false;
          }
          const data = snap.data() as { nodeSets?: ProposalNodeSet[] };
          if (!data?.nodeSets?.length) {
            alert('Live proposal has no structures / timber lists.');
            return false;
          }
          proposalSnapshot = { nodeSets: data.nodeSets };
          timberListSource = 'proposal_live';
          defaultLabel = 'Published from live proposal';
        } else if (pick === RFQ_PUBLISH_FROM_HUB) {
          const hubId = proj.timberProjectId?.trim();
          if (!hubId) {
            alert(
              'No Project Hub job is linked to this RFQ. The deal needs a Project Hub timber project (same deal) so the latest timber-list CSV can be loaded.',
            );
            return false;
          }
          if (hubPreload) {
            proposalSnapshot = { nodeSets: hubPreload.nodeSets };
            proposalVersionId = null;
            timberListSource = 'hub_csv';
            defaultLabel = `Published from Project Hub timber list (${hubPreload.sourceFileName})`;
          } else {
            const { nodeSets, sourceFileName } = await loadHubTimberListNodeSets(
              hubId,
              proj.timberProjectName || 'Project Hub timber list',
            );
            proposalSnapshot = { nodeSets };
            proposalVersionId = null;
            timberListSource = 'hub_csv';
            defaultLabel = `Published from Project Hub timber list (${sourceFileName})`;
          }
        } else if (stagedReleaseId) {
          if (hubPreload) {
            proposalSnapshot = { nodeSets: hubPreload.nodeSets };
            proposalVersionId = null;
            timberListSource = 'hub_staged_release';
            hubFileReleaseId = stagedReleaseId;
            defaultLabel = `Published from staged hub release (${hubPreload.sourceFileName})`;
          } else {
            const { nodeSets, sourceFileName } = await loadStagedTimberListNodeSets(
              proj.id,
              stagedReleaseId,
              proj.timberProjectName || 'Staged hub timber list',
            );
            proposalSnapshot = { nodeSets };
            proposalVersionId = null;
            timberListSource = 'hub_staged_release';
            hubFileReleaseId = stagedReleaseId;
            defaultLabel = `Published from staged hub release (${sourceFileName})`;
          }
        } else {
          proposalVersionId = pick;
          const vref = await getDoc(doc(db, 'proposalBuilders', targetDealId, 'versions', pick));
          if (!vref.exists()) {
            alert('Version not found.');
            return false;
          }
          const v = vref.data() as { snapshot?: { nodeSets?: ProposalNodeSet[] } };
          const ns = v?.snapshot?.nodeSets;
          if (!ns?.length) {
            alert('Version has no timber data.');
            return false;
          }
          proposalSnapshot = { nodeSets: ns };
          timberListSource = 'proposal_saved_version';
          defaultLabel = 'Published from saved proposal version';
        }

        let releaseVersionIndex: number | undefined;
        if (hubFileReleaseId) {
          const hubRelease = await getRfqHubFileRelease(proj.id, hubFileReleaseId);
          releaseVersionIndex = hubRelease?.releaseVersionIndex;
          const hubLabel = (hubRelease?.displayLabel || hubRelease?.fileName || '').trim();
          if (hubLabel) releaseDisplayLabel = hubLabel;
          if (hubRelease) {
            const linkFields = publishLinkFieldsFromHubRelease(hubRelease);
            proposalVersionId = linkFields.proposalVersionId;
            timberListSource = linkFields.timberListSource;
            linkedProposalVersionLabel = linkFields.linkedProposalVersionLabel;
            linkedProposalVersionSource = linkFields.linkedProposalVersionSource;
            linkedProposalVersionSavedAt = linkFields.linkedProposalVersionSavedAt;
            linkedFrom = linkFields.linkedFrom;
            const proposalLabel = (linkFields.linkedProposalVersionLabel || '').trim();
            if (proposalLabel) {
              defaultLabel = `Published from ${proposalLabel}`;
            }
          }
        }

        const custom = (packageTitleDraftByProject[proj.id] || '').trim();
        const label = custom || defaultLabel;

        if (releaseVersionIndex == null) {
          releaseVersionIndex = nextVendorPackageVersionIndex(proj.id, rfqPackages);
        }

        let publishTargets = targets;
        if (hubFileReleaseId) {
          if (hubReleaseAlreadyPublishedForTargets(rfqPackages, proj.id, hubFileReleaseId, targets)) {
            alert('Quote requests were already sent for this release.');
            return false;
          }
          publishTargets = filterTargetsWithoutHubReleasePackage(
            rfqPackages,
            proj.id,
            hubFileReleaseId,
            targets,
          );
          if (!publishTargets.length) {
            alert('Quote requests were already sent for this release.');
            return false;
          }
        }

        await publishRfqPackage({
          rfqProjectId: proj.id,
          supplierOrgId: proj.supplierOrgId,
          targetSupplierOrgIds: publishTargets,
          dealId: targetDealId,
          dealTitle: dealTitleForRfqPublish(proj),
          proposalVersionId,
          proposalSnapshot,
          label,
          releaseDisplayLabel,
          timberListSource,
          hubFileReleaseId,
          releaseVersionIndex,
          linkedProposalVersionId: proposalVersionId,
          linkedProposalVersionLabel,
          linkedProposalVersionSource,
          linkedProposalVersionSavedAt,
          linkedFrom,
          customEmailMessage,
          scopeStructuresByOrgId: scopeStructuresByOrg,
          ...publishShipToPayload(proj, publishTargets, 'timber_rfq'),
          createdByUid: user.id,
          createdByName: user.name || user.email,
        });
        return true;
      } catch (e) {
        console.error(e);
        alert(e instanceof Error ? e.message : 'Publish failed — see console.');
        return false;
      } finally {
        setPublishBusyId(null);
      }
    },
    [user, versionPickByProject, packageTitleDraftByProject, rfqPackages, publishRfqPackage, publishShipToPayload, dealTitleForRfqPublish],
  );

  const handlePublishCnc = useCallback(
    async (
      proj: RfqProject,
      targets: string[],
      selectedHubReleaseIds: string[],
      customEmailMessage?: string,
      scopeStructuresByOrg?: Record<string, TimberSupplierStructureRef[]>,
    ): Promise<boolean> => {
      if (!proj.id || !user?.id) return false;
      if (!targets.length) {
        alert('Select at least one CNC-capable vendor to publish to.');
        return false;
      }
      if (!selectedHubReleaseIds.length) {
        alert('Select at least one staged .3d model file to include in this publish.');
        return false;
      }
      setPublishBusyId(proj.id);
      const invitePatches = new Map<string, CncSpeciesAssignment[]>();
      try {
        for (const supplierOrgId of targets) {
          const label = 'CNC quote request';
          const invite = proj.timberSupplierInvites?.find(row => row.supplierOrgId === supplierOrgId);
          const speciesAssignments = cncAssignmentsCarriedForward(
            invite?.cncSpeciesAssignments,
            rfqPackages,
            proj.id,
            supplierOrgId,
          );
          if (cncMaterialFlowChanged(invite?.cncSpeciesAssignments, speciesAssignments)) {
            invitePatches.set(supplierOrgId, speciesAssignments ?? []);
          }
          const publishScope = scopeStructuresByOrg?.[supplierOrgId];
          let structuresForPayload = publishScope?.length ? publishScope : undefined;
          if (!structuresForPayload?.length) {
            const scopedStructures = inviteHasStructureScope(invite) ? invite!.structures : undefined;
            structuresForPayload = scopedStructures?.length ? scopedStructures : undefined;
          }

          const releasedFileIds: string[] = [];
          let releaseVersionIndex: number | undefined;
          let releaseDisplayLabel: string | undefined;
          for (const hubReleaseId of selectedHubReleaseIds) {
            const promoted = await promoteHubReleaseToVendor({
              rfqProjectId: proj.id,
              hubReleaseId,
              supplierOrgId,
              dealId: proj.dealId,
            });
            releasedFileIds.push(promoted.releaseId);
            if (releaseVersionIndex == null) {
              const hubRelease = await getRfqHubFileRelease(proj.id, hubReleaseId);
              releaseVersionIndex = hubRelease?.releaseVersionIndex;
              const hubLabel = (hubRelease?.displayLabel || hubRelease?.fileName || '').trim();
              if (hubLabel) releaseDisplayLabel = hubLabel;
            }
          }
          if (releaseVersionIndex == null) {
            releaseVersionIndex = nextVendorPackageVersionIndex(proj.id, rfqPackages);
          }

          const primaryHubReleaseId =
            selectedHubReleaseIds.length === 1 ? selectedHubReleaseIds[0] : undefined;
          if (
            primaryHubReleaseId
            && hubReleaseAlreadyPublishedForTargets(rfqPackages, proj.id, primaryHubReleaseId, [
              supplierOrgId,
            ])
          ) {
            continue;
          }

          await publishRfqPackage({
            rfqProjectId: proj.id,
            supplierOrgId: proj.supplierOrgId,
            targetSupplierOrgIds: [supplierOrgId],
            dealId: proj.dealId,
            dealTitle: dealTitleForRfqPublish(proj),
            workType: 'cnc_quote',
            label,
            releaseDisplayLabel,
            hubFileReleaseId: selectedHubReleaseIds.length === 1 ? selectedHubReleaseIds[0] : undefined,
            releaseVersionIndex,
            cncPackagePayload: {
              boardFeet: cncInviteBoardFeet(invite, proj.boardFeet ?? 0),
              releasedFileIds,
              ...(speciesAssignments?.length ? { speciesAssignments } : {}),
              ...(structuresForPayload?.length ? { structures: structuresForPayload } : {}),
            },
            customEmailMessage,
            scopeStructuresByOrgId: structuresForPayload?.length
              ? { [supplierOrgId]: structuresForPayload }
              : scopeStructuresByOrg,
            ...publishShipToPayload(proj, [supplierOrgId], 'cnc_quote'),
            createdByUid: user.id,
            createdByName: user.name || user.email,
          });
        }
        let timberSupplierInvites = proj.timberSupplierInvites;
        invitePatches.forEach((assignments, orgId) => {
          timberSupplierInvites = patchTimberSupplierInvite(timberSupplierInvites, orgId, {
            cncSpeciesAssignments: assignments,
          });
        });
        await updateRfqProject(proj.id, {
          workType: 'cnc_quote',
          ...(invitePatches.size ? { timberSupplierInvites } : {}),
        });
        return true;
      } catch (e) {
        console.error(e);
        alert(e instanceof Error ? e.message : 'CNC publish failed — see console.');
        return false;
      } finally {
        setPublishBusyId(null);
      }
    },
    [user, supplierOrgById, timberProject, rfqPackages, publishRfqPackage, updateRfqProject, publishShipToPayload, dealTitleForRfqPublish],
  );

  const openHubPublishPreview = useCallback(
    async (proj: RfqProject, targets: string[]) => {
      const filtered = filterPublishTargets(proj, targets);
      if (!filtered.length) {
        alert('Select at least one supplier to publish to.');
        return;
      }
      if (!ensureTargetsHaveNotificationFirstName(filtered)) return;
      const hubId = proj.timberProjectId?.trim();
      if (!hubId) {
        alert(
          'No Project Hub job is linked to this RFQ. The deal needs a Project Hub timber project (same deal) so the latest timber-list CSV can be loaded.',
        );
        return;
      }
      setPublishReview({
        open: true,
        loading: true,
        error: null,
        data: null,
        pending: null,
      });
      try {
        const timberPreview = await loadHubTimberListPublishPreview(
          hubId,
          proj.timberProjectName || 'Project Hub timber list',
        );
        const preload = { nodeSets: timberPreview.nodeSets, sourceFileName: timberPreview.sourceFileName };
        setPublishReview({
          open: true,
          loading: false,
          error: null,
          data: {
            workType: 'timber_rfq',
            project: proj,
            dealTitle: dealTitleForRfqPublish(proj),
            releaseVersionIndex: 0,
            releaseLabel: 'Latest Project Hub timber list',
            hubFileReleaseId: '',
            targets: filtered,
            timberPreview,
            preload,
            ...publishReviewSharedFields(proj, filtered, 'timber_rfq'),
            publishStructureOptions: structureRefsFromNodeSets(timberPreview.nodeSets),
          },
          pending: {
            kind: 'timber',
            proj,
            targets: filtered,
            pick: RFQ_PUBLISH_FROM_HUB,
            preload,
          },
        });
      } catch (e) {
        console.error(e);
        setPublishReview({
          open: true,
          loading: false,
          error: e instanceof Error ? e.message : 'Could not load hub timber CSV.',
          data: null,
          pending: null,
        });
      }
    },
    [filterPublishTargets, ensureTargetsHaveNotificationFirstName, publishReviewSharedFields, dealTitleForRfqPublish],
  );

  const openStagedPublishPreview = useCallback(
    async (proj: RfqProject, targets: string[], hubFileReleaseId: string) => {
      if (!proj.id) return;
      const filtered = pendingPublishTargetsForRelease(proj, hubFileReleaseId, targets);
      if (!filtered.length) {
        alert('Quote requests were already sent to all selected suppliers for this release.');
        return;
      }
      if (!ensureTargetsHaveNotificationFirstName(filtered)) return;
      setPublishReview({
        open: true,
        loading: true,
        error: null,
        data: null,
        pending: null,
      });
      try {
        const timberPreview = await loadStagedTimberListPublishPreview(
          proj.id,
          hubFileReleaseId,
          proj.timberProjectName || 'Staged hub timber list',
        );
        const preload = { nodeSets: timberPreview.nodeSets, sourceFileName: timberPreview.sourceFileName };
        const pick = `${RFQ_PUBLISH_STAGED_PREFIX}${hubFileReleaseId}`;
        setPublishReview({
          open: true,
          loading: false,
          error: null,
          data: {
            workType: 'timber_rfq',
            project: proj,
            dealTitle: dealTitleForRfqPublish(proj),
            releaseVersionIndex: 0,
            releaseLabel: timberPreview.sourceFileName,
            hubFileReleaseId,
            targets: filtered,
            timberPreview,
            preload,
            ...publishReviewSharedFields(proj, filtered, 'timber_rfq'),
            publishStructureOptions: structureRefsFromNodeSets(timberPreview.nodeSets),
          },
          pending: {
            kind: 'timber',
            proj,
            targets: filtered,
            pick,
            preload,
          },
        });
      } catch (e) {
        console.error(e);
        setPublishReview({
          open: true,
          loading: false,
          error: e instanceof Error ? e.message : 'Could not load staged timber CSV.',
          data: null,
          pending: null,
        });
      }
    },
    [pendingPublishTargetsForRelease, ensureTargetsHaveNotificationFirstName, publishReviewSharedFields, dealTitleForRfqPublish],
  );

  const requestTimberPublishReview = useCallback(
    async (
      proj: RfqProject,
      targets: string[],
      release: RfqHubFileRelease,
      _preload: { nodeSets: ProposalNodeSet[]; sourceFileName: string },
    ) => {
      if (!proj.id || !release.id || !user?.id) return;
      const filtered = pendingPublishTargetsForRelease(proj, release.id, targets);
      if (!filtered.length) {
        alert('Quote requests were already sent to all selected suppliers for this release.');
        return;
      }
      if (!ensureTargetsHaveNotificationFirstName(filtered)) return;
      setPublishReview({
        open: true,
        loading: true,
        error: null,
        data: null,
        pending: null,
      });
      try {
        let proposalNodeSets: ProposalNodeSet[] | null = null;
        if (proj.dealId) {
          const snap = await getDoc(doc(db, 'proposalBuilders', proj.dealId));
          if (snap.exists()) {
            const data = snap.data() as { nodeSets?: ProposalNodeSet[] };
            proposalNodeSets = data?.nodeSets ?? null;
          }
        }
        const timberPreview = await loadStagedTimberListPublishPreview(
          proj.id,
          release.id,
          proj.timberProjectName || 'Staged hub timber list',
          proposalNodeSets,
        );
        const preload = { nodeSets: timberPreview.nodeSets, sourceFileName: timberPreview.sourceFileName };
        const pick = `${RFQ_PUBLISH_STAGED_PREFIX}${release.id}`;
        setPublishReview({
          open: true,
          loading: false,
          error: null,
          data: {
            workType: 'timber_rfq',
            project: proj,
            dealTitle: dealTitleForRfqPublish(proj),
            releaseVersionIndex: release.releaseVersionIndex,
            releaseLabel: release.displayLabel || release.fileName,
            hubFileReleaseId: release.id,
            proposalLinkText: formatProposalRfqLinkText(proposalRfqLinkFromRelease(release)),
            targets: filtered,
            timberPreview,
            preload,
            ...publishReviewSharedFields(proj, filtered, 'timber_rfq'),
            publishStructureOptions: structureRefsFromNodeSets(timberPreview.nodeSets),
            stagedStructures: stagedReleaseStructureRefs(release),
            referenceRelease: isReferenceRelease(release),
          },
          pending: {
            kind: 'timber',
            proj,
            targets: filtered,
            pick,
            preload,
          },
        });
      } catch (e) {
        console.error(e);
        setPublishReview({
          open: true,
          loading: false,
          error: e instanceof Error ? e.message : 'Could not load publish preview.',
          data: null,
          pending: null,
        });
      }
    },
    [user?.id, pendingPublishTargetsForRelease, ensureTargetsHaveNotificationFirstName, publishReviewSharedFields, dealTitleForRfqPublish],
  );

  const requestCncPublishReview = useCallback(
    async (proj: RfqProject, targets: string[], release: RfqHubFileRelease) => {
      if (!proj.id || !release.id || !user?.id) return;
      const filtered = pendingPublishTargetsForRelease(proj, release.id, targets);
      if (!filtered.length) {
        alert(
          isReferenceRelease(release)
            ? 'This reference release was already shared with all selected suppliers.'
            : 'Quote requests were already sent to all selected suppliers for this release.',
        );
        return;
      }
      if (!ensureTargetsHaveNotificationFirstName(filtered)) return;
      setPublishReview({
        open: true,
        loading: true,
        error: null,
        data: null,
        pending: null,
      });
      try {
        let publishStructureOptions: TimberSupplierStructureRef[] = [];
        if (proj.dealId) {
          const { loadDealProposalStructures } = await import('../utils/loadDealProposalStructures');
          publishStructureOptions = await loadDealProposalStructures(proj.dealId, { hubProjectId: proj.timberProjectId });
        }
        const shared = publishReviewSharedFields(proj, filtered, 'cnc_quote');
        setPublishReview({
          open: true,
          loading: false,
          error: null,
          data: {
            workType: 'cnc_quote',
            project: proj,
            dealTitle: dealTitleForRfqPublish(proj),
            release,
            targets: filtered,
            ...shared,
            publishStructureOptions,
            stagedStructures: stagedReleaseStructureRefs(release),
            referenceRelease: isReferenceRelease(release),
          },
          pending: {
            kind: 'cnc',
            proj,
            targets: filtered,
            releaseId: release.id,
          },
        });
      } catch (e) {
        console.error(e);
        setPublishReview({
          open: true,
          loading: false,
          error: e instanceof Error ? e.message : 'Could not load proposal structures.',
          data: null,
          pending: null,
        });
      }
    },
    [user?.id, pendingPublishTargetsForRelease, ensureTargetsHaveNotificationFirstName, publishReviewSharedFields, dealTitleForRfqPublish],
  );

  /**
   * "Resend — scope changed": review + resend for a supplier whose structure
   * selection drifted from their already-published package. Deliberately
   * bypasses pendingPublishTargetsForRelease — that filter removes orgs that
   * already have a package for the release, which is exactly who we target.
   */
  const requestScopeChangeReview = useCallback(
    async (
      proj: RfqProject,
      orgId: string,
      release: RfqHubFileRelease,
      workType: 'timber_rfq' | 'cnc_quote',
    ) => {
      if (!proj.id || !release.id || !user?.id) return;
      const pkg: RfqPackage | undefined =
        rfqPackages.find(
          p =>
            p.rfqProjectId === proj.id
            && p.supplierOrgId === orgId
            && (p.workType ?? 'timber_rfq') === workType
            && packageCoversHubRelease(p, release.id),
        )
        ?? rfqPackages
          .filter(
            p =>
              p.rfqProjectId === proj.id
              && p.supplierOrgId === orgId
              && (p.workType ?? 'timber_rfq') === workType
              && !p.legacyImport,
          )
          .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime())[0];
      if (!pkg?.id) {
        alert('No published quote request found for this supplier on this release.');
        return;
      }
      if (pkg.legacyImport) {
        alert('This is a legacy imported package — there is no supplier notification to resend.');
        return;
      }
      if (!ensureTargetsHaveNotificationFirstName([orgId])) return;

      const invite = proj.timberSupplierInvites?.find(row => row.supplierOrgId === orgId);
      const carriedCncAssignments = proj.id
        ? cncAssignmentsCarriedForward(invite?.cncSpeciesAssignments, rfqPackages, proj.id, orgId)
        : invite?.cncSpeciesAssignments;
      const summary = scopeChangeSummary(pkg, invite);
      const scopeStructures = inviteHasStructureScope(invite) ? invite!.structures! : [];
      const addedStructureNames = summary.added.map(s => s.name).filter(Boolean);
      const removedStructureNames = summary.removed;
      const speciesChangeLines = summary.speciesChanges;
      const inviteStructureSpecies = normalizeStructureSpecies(invite?.structureSpecies, invite?.requestedSpecies ?? []);

      if (workType === 'cnc_quote') {
        setPublishReview({
          open: true,
          loading: true,
          error: null,
          data: null,
          pending: null,
        });
        try {
          let publishStructureOptions: TimberSupplierStructureRef[] = [];
          if (proj.dealId) {
            const { loadDealProposalStructures } = await import('../utils/loadDealProposalStructures');
            publishStructureOptions = await loadDealProposalStructures(proj.dealId, { hubProjectId: proj.timberProjectId });
          }
          const shared = publishReviewSharedFields(proj, [orgId], 'cnc_quote');
          setPublishReview({
            open: true,
            loading: false,
            error: null,
            data: {
              workType: 'cnc_quote',
              mode: 'scope_change',
              project: proj,
              dealTitle: dealTitleForRfqPublish(proj),
              release,
              targets: [orgId],
              scopeChange: { added: addedStructureNames, removed: removedStructureNames },
              ...shared,
              publishStructureOptions,
            },
            pending: {
              kind: 'scope_change',
              proj,
              orgId,
              packageId: pkg.id,
              workType,
              scopeStructures,
              cncSpeciesAssignments: carriedCncAssignments?.length
                ? carriedCncAssignments
                : undefined,
              cncBoardFeet: cncInviteBoardFeet(invite, proj.boardFeet ?? 0),
              addedStructureNames,
              removedStructureNames,
            },
          });
        } catch (e) {
          console.error(e);
          setPublishReview({
            open: true,
            loading: false,
            error: e instanceof Error ? e.message : 'Could not load proposal structures.',
            data: null,
            pending: null,
          });
        }
        return;
      }

      setPublishReview({
        open: true,
        loading: true,
        error: null,
        data: null,
        pending: null,
      });
      try {
        let proposalNodeSets: ProposalNodeSet[] | null = null;
        if (proj.dealId) {
          const snap = await getDoc(doc(db, 'proposalBuilders', proj.dealId));
          if (snap.exists()) {
            const data = snap.data() as { nodeSets?: ProposalNodeSet[] };
            proposalNodeSets = data?.nodeSets ?? null;
          }
        }
        // A Files → RFQs package spans one release per structure; rebuild from all of them.
        const packageReleaseIds = packageHubReleaseIds(pkg);
        const timberPreview = await loadPackageTimberListPublishPreview(
          proj.id,
          packageReleaseIds.length ? packageReleaseIds : [release.id],
          proj.timberProjectName || 'Staged hub timber list',
          proposalNodeSets,
        );
        const preload = { nodeSets: timberPreview.nodeSets, sourceFileName: timberPreview.sourceFileName };
        const speciesRules = structureSpeciesRulesFromInvite(invite, structureRefsFromNodeSets(timberPreview.nodeSets));
        const timberStructureSpecies = speciesRules.length
          ? timberPreview.nodeSets.map(ns => ({
              name: ns.name,
              species: speciesForStructure(ns, invite?.requestedSpecies ?? [], speciesRules),
            }))
          : undefined;
        setPublishReview({
          open: true,
          loading: false,
          error: null,
          data: {
            workType: 'timber_rfq',
            mode: 'scope_change',
            project: proj,
            dealTitle: dealTitleForRfqPublish(proj),
            releaseVersionIndex: release.releaseVersionIndex,
            releaseLabel: release.displayLabel || release.fileName,
            hubFileReleaseId: release.id,
            proposalLinkText: formatProposalRfqLinkText(proposalRfqLinkFromRelease(release)),
            targets: [orgId],
            timberPreview,
            preload,
            scopeChange: { added: addedStructureNames, removed: removedStructureNames, speciesChanges: speciesChangeLines },
            timberStructureSpecies,
            ...publishReviewSharedFields(proj, [orgId], 'timber_rfq'),
            publishStructureOptions: structureRefsFromNodeSets(timberPreview.nodeSets),
          },
          pending: {
            kind: 'scope_change',
            proj,
            orgId,
            packageId: pkg.id,
            workType,
            scopeStructures,
            releaseNodeSets: timberPreview.nodeSets,
            addedStructureNames,
            removedStructureNames,
            structureSpecies: inviteStructureSpecies ?? null,
            speciesChangeLines,
          },
        });
      } catch (e) {
        console.error(e);
        setPublishReview({
          open: true,
          loading: false,
          error: e instanceof Error ? e.message : 'Could not load the scope-change preview.',
          data: null,
          pending: null,
        });
      }
    },
    [user?.id, rfqPackages, ensureTargetsHaveNotificationFirstName, publishReviewSharedFields, dealTitleForRfqPublish],
  );

  const dismissScopeChange = useCallback(
    async (
      proj: RfqProject,
      orgId: string,
      release: RfqHubFileRelease,
      workType: 'timber_rfq' | 'cnc_quote',
    ) => {
      if (!proj.id || !release.id || !user?.id) return false;
      const pkg =
        rfqPackages.find(
          p =>
            p.rfqProjectId === proj.id
            && p.supplierOrgId === orgId
            && (p.workType ?? 'timber_rfq') === workType
            && packageCoversHubRelease(p, release.id),
        )
        ?? rfqPackages
          .filter(
            p =>
              p.rfqProjectId === proj.id
              && p.supplierOrgId === orgId
              && (p.workType ?? 'timber_rfq') === workType
              && !p.legacyImport,
          )
          .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime())[0];
      if (!pkg?.id) {
        alert('No published quote request found for this supplier on this release.');
        return false;
      }
      if (pkg.legacyImport) {
        alert('This is a legacy imported package — scope cannot be dismissed.');
        return false;
      }

      const invite = proj.timberSupplierInvites?.find(row => row.supplierOrgId === orgId);
      const scopeStructures = inviteHasStructureScope(invite) ? invite!.structures! : [];
      const carriedCncAssignments = proj.id
        ? cncAssignmentsCarriedForward(invite?.cncSpeciesAssignments, rfqPackages, proj.id, orgId)
        : invite?.cncSpeciesAssignments;

      setPublishBusyId(proj.id);
      try {
        if (workType === 'cnc_quote') {
          await acknowledgeRfqPackageScopeDrift({
            packageId: pkg.id,
            scopeStructures: scopeStructures.length ? scopeStructures : undefined,
            cncSpeciesAssignments: carriedCncAssignments?.length
              ? carriedCncAssignments
              : undefined,
            cncBoardFeet: cncInviteBoardFeet(invite, proj.boardFeet ?? 0),
          });
          if (
            proj.id
            && cncMaterialFlowChanged(invite?.cncSpeciesAssignments, carriedCncAssignments)
          ) {
            await updateRfqProject(proj.id, {
              timberSupplierInvites: patchTimberSupplierInvite(proj.timberSupplierInvites, orgId, {
                cncSpeciesAssignments: carriedCncAssignments ?? [],
              }),
            });
          }
          return true;
        }

        let proposalNodeSets: ProposalNodeSet[] = [];
        if (proj.dealId) {
          const snap = await getDoc(doc(db, 'proposalBuilders', proj.dealId));
          if (snap.exists()) {
            proposalNodeSets = (snap.data() as { nodeSets?: ProposalNodeSet[] }).nodeSets ?? [];
          }
        }
        // Same rule as the resend path: every release the package spans, not just the clicked one.
        const packageReleaseIds = packageHubReleaseIds(pkg);
        const { nodeSets: releaseNodeSets } = await loadPackageTimberListNodeSets(
          proj.id,
          packageReleaseIds.length ? packageReleaseIds : [release.id],
          proposalNodeSets,
        );
        const all = releaseNodeSets.length ? releaseNodeSets : proposalNodeSets;
        if (!all.length) {
          alert('This timber list has no rows to keep on the package.');
          return false;
        }
        await acknowledgeRfqPackageScopeDrift({
          packageId: pkg.id,
          scopeStructures: scopeStructures.length ? scopeStructures : undefined,
          proposalNodeSets: all,
          structureSpecies: normalizeStructureSpecies(invite?.structureSpecies, invite?.requestedSpecies ?? []) ?? null,
        });
        return true;
      } catch (e) {
        console.error(e);
        alert(
          e instanceof Error
            ? `Could not dismiss scope reminder: ${e.message}`
            : 'Could not dismiss scope reminder.',
        );
        return false;
      } finally {
        setPublishBusyId(null);
      }
    },
    [user?.id, rfqPackages, updateRfqProject],
  );

  const handleScopeChangeResend = useCallback(
    async (pending: PendingScopeChangeResend, customEmailMessage?: string): Promise<boolean> => {
      if (!pending.proj.id) return false;
      setPublishBusyId(pending.proj.id);
      try {
        let proposalNodeSets: ProposalNodeSet[] | undefined;
        if (pending.workType === 'timber_rfq') {
          proposalNodeSets = pending.releaseNodeSets ?? [];
        }
        const result = await resendRfqPackageScopeChange({
          packageId: pending.packageId,
          scopeStructures: pending.scopeStructures.length ? pending.scopeStructures : undefined,
          proposalNodeSets,
          cncSpeciesAssignments: pending.cncSpeciesAssignments,
          cncBoardFeet: pending.cncBoardFeet,
          addedStructureNames: pending.addedStructureNames,
          removedStructureNames: pending.removedStructureNames,
          ...(pending.workType === 'timber_rfq' ? { structureSpecies: pending.structureSpecies ?? null } : {}),
          speciesChangeLines: pending.speciesChangeLines,
          customMessage: customEmailMessage,
        });
        if (!result.ok) {
          alert('Scope update failed — see console.');
          return false;
        }
        return true;
      } catch (e) {
        console.error(e);
        alert(
          e instanceof Error
            ? `Scope update failed: ${e.message}`
            : 'Scope update failed — see console.',
        );
        return false;
      } finally {
        setPublishBusyId(null);
      }
    },
    [],
  );

  const requestPublish = useCallback(
    (proj: RfqProject, targets: string[], pickOverride?: string) => {
      if (!proj.id || !user?.id) return;
      const allowed = rfqProjectSupplierOrgIds(proj);
      const filtered = targets.filter(id => allowed.includes(id));
      if (!filtered.length) {
        alert('Select at least one supplier to publish to.');
        return;
      }
      const pick = pickOverride ?? (versionPickByProject[proj.id] || 'live');
      if (pick === RFQ_PUBLISH_FROM_HUB) {
        void openHubPublishPreview(proj, filtered);
        return;
      }
      const stagedReleaseId = parseStagedReleasePick(pick);
      if (stagedReleaseId) {
        void openStagedPublishPreview(proj, filtered, stagedReleaseId);
        return;
      }
      void handlePublish(proj, filtered, null, pick);
    },
    [user?.id, versionPickByProject, openHubPublishPreview, openStagedPublishPreview, handlePublish],
  );

  const handlePublishReviewConfirm = useCallback(
    async (
      customEmailMessage: string,
      scopeStructuresByOrg: Record<string, TimberSupplierStructureRef[]>,
    ) => {
      const { pending } = publishReview;
      if (!pending) return;
      const msg = customEmailMessage.trim() || undefined;
      let ok = false;
      if (pending.kind === 'timber') {
        setVersionPickByProject(m => ({ ...m, [pending.proj.id!]: pending.pick }));
        ok = await handlePublish(
          pending.proj,
          pending.targets,
          pending.preload,
          pending.pick,
          msg,
          scopeStructuresByOrg,
        );
      } else if (pending.kind === 'scope_change') {
        const scopeStructures =
          scopeStructuresByOrg[pending.orgId] ?? pending.scopeStructures;
        ok = await handleScopeChangeResend({ ...pending, scopeStructures }, msg);
      } else {
        ok = await handlePublishCnc(
          pending.proj,
          pending.targets,
          [pending.releaseId],
          msg,
          scopeStructuresByOrg,
        );
      }
      if (ok) setPublishReview(CLOSED_PUBLISH_REVIEW);
    },
    [publishReview, handlePublish, handlePublishCnc, handleScopeChangeResend],
  );

  return {
    dealId,
    supplierOrgs,
    supplierInvites,
    supplierOrgById,
    rfqPackages,
    rfqSubmissions,
    timberProject,
    cncProject,
    otherProject,
    selectedDeal,
    selectedTimberProject,
    projectDisplay,
    projectSite,
    dealVendorSummaries,
    versionsByDeal,
    publishBusyId,
    publishReview,
    setPublishReview,
    packageTitleDraftByProject,
    setPackageTitleDraftByProject,
    versionPickByProject,
    setVersionPickByProject,
    updateRfqProject,
    deleteRfqPackage,
    openSubmissionFile,
    submissionPdfViewer,
    closeSubmissionPdfViewer,
    deleteSubmission,
    updateQuoteSubmission,
    loadVersions,
    handleSyncHubLink,
    requestPublish,
    requestTimberPublishReview,
    requestCncPublishReview,
    requestScopeChangeReview,
    dismissScopeChange,
    handlePublishReviewConfirm,
  };
}
